import { observer } from 'mobx-react-lite'
import { type ReactElement, type ReactNode, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useSettings } from 'app/hooks/useSettings'
import { useFeatureToggleStore, useListStore } from 'app/stores/StoreContext'
import type { NavListsSetting } from 'app/types/navigation'
import { useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { isBarePipelinesPath, legacyListRedirect } from 'lib/legacyListRedirect'
import { firstManualListPath } from 'lib/lists'
import { navListsSettingKey } from 'lib/navLists'

/** Params worth carrying onto the list a legacy URL now means. */
const KEPT_PARAMS = [`wId`, `drawer`]

export function withKeptParams(path: string, search: string): string {
  const from = new URLSearchParams(search)
  const kept = new URLSearchParams()
  for (const key of KEPT_PARAMS) {
    const value = from.get(key)
    if (value) kept.set(key, value)
  }
  const rest = kept.toString()
  return rest ? `${path}?${rest}` : path
}

/**
 * Where the bare Pipelines home lands. Its own component so the saved Lists order is only
 * read once grouped navigation is on: a toggle-off user never asks for a setting they cannot use.
 */
const PipelinesLanding = observer(function PipelinesLanding(): ReactElement {
  const listStore = useListStore()
  const location = useLocation()
  const workspace = useWorkspace()
  // The Lists order is the sidebar's own, so the list `/pipelines` opens is the one
  // sitting at the top of it (see useNavLists for the key).
  const [navLists, , navListsLoaded] = useSettings<NavListsSetting>(navListsSettingKey(workspace?.id))
  if (!navListsLoaded) return <></>
  // A read that failed has no lists to offer, so Action Center is the honest landing
  // rather than a page that never resolves.
  if (listStore.list === undefined && !listStore.listError) return <></>
  const first = firstManualListPath(listStore.hits, navLists?.order ?? [])
  return <Navigate to={withKeptParams(first ?? `/action-center`, location.search)} replace />
})

/**
 * Old queue, system pipeline and preset tab URLs land on their list page once
 * grouped navigation and Action Center are both on; otherwise the wrapped page renders as before.
 */
export const LegacyListRoute = observer(function LegacyListRoute({ children }: { children: ReactNode }): ReactElement {
  const store = useFeatureToggleStore()
  const listStore = useListStore()
  const groupedNavigation = useFeatureToggle(`grouped-navigation`)
  const actionCenter = useFeatureToggle(`action-center`)
  const listPagesEnabled = groupedNavigation && actionCenter
  const location = useLocation()
  // Only the Pipelines home has no list of its own in the URL, so only it has to ask
  // the workspace which list it now means.
  const needsLists =
    listPagesEnabled && isBarePipelinesPath(location.pathname) && !new URLSearchParams(location.search).get(`tab`)

  useEffect(() => {
    bg(() => store.load())
  }, [store])

  useEffect(() => {
    // A failed read leaves `list` undefined with `listError` set: without that last guard
    // the loading flag falling back to false would re-run this effect and ask again forever.
    if (needsLists && listStore.list === undefined && !listStore.listLoading && !listStore.listError)
      bg(() => listStore.fetchList())
  }, [needsLists, listStore, listStore.list, listStore.listLoading, listStore.listError])

  // Nothing until the toggles are in, like FeatureRoute: redirecting on the
  // default-false would send toggle-on users to the old page on every reload.
  if (!store.loaded) return <></>
  const target = listPagesEnabled ? legacyListRedirect(location.pathname, location.search) : undefined
  if (target) return <Navigate to={target} replace />
  if (needsLists) return <PipelinesLanding />
  return <>{children}</>
})
